import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import VoiceRecorder from "@/components/patient/VoiceRecorder";
import { Mic, Music, Play, Pause, Trash2, HeartHandshake, MessageCircleHeart } from "lucide-react";
import VoiceAlbum from "@/components/family/VoiceAlbum";
import { GardienAvatar } from "@/components/GardienBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const audioSrc = (u) => (u.startsWith("http") ? u : `${BACKEND}${u}`);

export function MiniPlay({ url, testId, className = "" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    if (playing) { ref.current.pause(); setPlaying(false); return; }
    document.querySelectorAll("audio").forEach((a) => a !== ref.current && a.pause());
    ref.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  return (
    <>
      <audio ref={ref} src={audioSrc(url)} preload="none" onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} />
      <button onClick={toggle} data-testid={testId} aria-label={playing ? "Pause" : "Écouter"}
        className={`w-11 h-11 rounded-full bg-emerald-700 text-white flex items-center justify-center shrink-0 active:scale-95 transition-transform ${className}`}>
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>
    </>
  );
}

const daysSince = (m) => (m ? Math.floor((Date.now() - new Date(m.created_at).getTime()) / 86400000) : Infinity);
const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });

function MessageSection({ data, defaultName, patientName, onChanged }) {
  const [rec, setRec] = useState(false);
  return (
    <div className="rounded-3xl bg-white border border-border/60 p-6" data-testid="family-message-section">
      <div className="flex items-center gap-3 mb-3">
        <HeartHandshake className="w-6 h-6 text-rose-400" />
        <h3 className="font-serif-display text-2xl">Le mot doux du jour</h3>
      </div>
      <p className="text-gray-500 mb-4">{patientName} l'écoutera sur son écran d'accueil. Le dernier message enregistré reste affiché.</p>
      {daysSince(data) >= 7 && !rec && (
        <div className="flex items-center gap-3 rounded-2xl bg-sky-50 border border-sky-200 p-4 mb-4 soft-in" data-testid="family-message-reminder">
          <GardienAvatar id="rose-auguste" className="w-12 h-12" />
          <p className="text-gray-700 leading-snug">
            {data ? `Cela fait ${daysSince(data)} jours depuis le dernier mot doux.` : "Aucun mot doux n'a encore été laissé."}{" "}
            Quelques mots de votre voix feraient sûrement plaisir à {patientName}.
          </p>
        </div>
      )}
      {data && (
        <div className="flex items-center gap-3 rounded-2xl bg-warm/10 p-3 mb-4" data-testid="family-message-current">
          <MiniPlay url={data.audio_url} testId="family-message-play" />
          <span className="text-gray-700">Message de <b>{data.label}</b> · {fmtDate(data.created_at)}</span>
        </div>
      )}
      {rec ? (
        <VoiceRecorder postUrl="/family/message" title="Votre mot doux" defaultName={defaultName}
          intro={`Quelques mots tendres pour ${patientName} : un bonjour, une nouvelle, un « je pense à toi ».`}
          onClose={() => setRec(false)} onSaved={() => { setRec(false); onChanged(); }} />
      ) : (
        <button onClick={() => setRec(true)} data-testid="family-message-record"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-rose-500 text-white font-semibold hover:scale-105 transition-transform">
          <Mic className="w-5 h-5" /> {data ? "Enregistrer un nouveau mot doux" : "Enregistrer un mot doux"}
        </button>
      )}
    </div>
  );
}

function SongsSection({ songs, defaultName, patientName, onChanged }) {
  const [rec, setRec] = useState(false);
  const remove = async (s) => {
    if (!window.confirm(`Supprimer la chanson « ${s.song_title} » ?`)) return;
    try { await api.delete(`/family/recordings/${s.id}`); onChanged(); toast.success("Chanson supprimée"); }
    catch { toast.error("Suppression impossible."); }
  };
  return (
    <div className="rounded-3xl bg-white border border-border/60 p-6" data-testid="family-songs-section">
      <div className="flex items-center gap-3 mb-3">
        <Music className="w-6 h-6 text-amber-600" />
        <h3 className="font-serif-display text-2xl">Les chansons de la famille</h3>
      </div>
      <p className="text-gray-500 mb-4">Chantez une chanson qu'il ou elle aime : {patientName} pourra l'écouter et jouer à la reconnaître.</p>
      <div className="space-y-2 mb-4">
        {songs.map((s) => (
          <div key={s.id} className="flex items-center gap-3 rounded-2xl bg-amber-50 p-3" data-testid={`family-song-${s.id}`}>
            <MiniPlay url={s.audio_url} testId={`family-song-play-${s.id}`} />
            <span className="flex-1 text-gray-700"><b>{s.song_title}</b> · chantée par {s.label}</span>
            {s.mine && (
              <button onClick={() => remove(s)} className="w-10 h-10 rounded-full hover:bg-white flex items-center justify-center text-gray-400 hover:text-red-500" aria-label="Supprimer" data-testid={`family-song-delete-${s.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {rec ? (
        <VoiceRecorder postUrl="/family/songs" askTitle title="Votre chanson" defaultName={defaultName}
          intro="Chantez ou fredonnez tranquillement, même juste un refrain." onClose={() => setRec(false)}
          onSaved={() => { setRec(false); onChanged(); }} />
      ) : (
        <button onClick={() => setRec(true)} data-testid="family-song-record"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-amber-500 text-white font-semibold hover:scale-105 transition-transform">
          <Mic className="w-5 h-5" /> Enregistrer une chanson
        </button>
      )}
    </div>
  );
}

export default function FamilyVoices({ patientName, defaultName }) {
  const [message, setMessage] = useState(null);
  const [songs, setSongs] = useState([]);
  const [reply, setReply] = useState(null);
  const [album, setAlbum] = useState([]);
  const load = useCallback(() => {
    api.get("/family/message").then((r) => setMessage(r.data)).catch(() => {});
    api.get("/family/songs").then((r) => setSongs(r.data)).catch(() => {});
    api.get("/family/reply").then((r) => setReply(r.data)).catch(() => {});
    api.get("/family/album").then((r) => setAlbum(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      {reply && (
        <div className="rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-5 mb-6 flex items-center gap-4 soft-in" data-testid="family-reply">
          <MiniPlay url={reply.audio_url} testId="family-reply-play" className="w-14 h-14" />
          <span>
            <span className="flex items-center gap-2 font-serif-display text-xl text-gray-800"><MessageCircleHeart className="w-5 h-5 text-emerald-700" /> {patientName} vous a répondu</span>
            <span className="block text-sm text-gray-500">Message enregistré le {fmtDate(reply.created_at)}</span>
          </span>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6 mb-6 items-start" data-testid="family-voices">
        <MessageSection data={message} defaultName={defaultName} patientName={patientName} onChanged={load} />
        <SongsSection songs={songs} defaultName={defaultName} patientName={patientName} onChanged={load} />
      </div>
      <VoiceAlbum items={album} onChanged={load} />
    </>
  );
}
